import { queryClient } from '@/lib/queryClient';
import { create } from 'zustand';
import { getMe, logout, refreshTokens, type AuthTokens, type Me } from '@/api/auth';
import { adoptProfileLanguage } from '@/i18n';
import { registerPushToken } from '@/features/push/register';
import { useSettingsStore } from '@/features/settings/settingsStore';
import { clearRefreshToken, getRefreshToken, saveRefreshToken } from '@/lib/secureStore';

type Status = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: Status;
  /** Short-lived JWT, kept in memory only (never persisted). */
  accessToken: string | null;
  me: Me | null;

  /** Tokens held during onboarding (after register, before the user finishes the flow). */
  pendingTokens: AuthTokens | null;

  /** Run on app launch: try to restore a session from the stored refresh token. */
  bootstrap: () => Promise<void>;
  /** Persist the refresh token, load the profile, and mark authenticated. */
  signInWithTokens: (tokens: AuthTokens) => Promise<void>;
  /** Replace the cached profile (e.g. after editing it). */
  setMe: (me: Me) => void;
  /** Register succeeded — hold the tokens while the user completes onboarding (no sign-in yet). */
  startOnboarding: (tokens: AuthTokens) => void;
  /** Finish onboarding: actually sign in with the held tokens. */
  finishOnboarding: () => Promise<void>;
  /** Revoke the session server-side (best effort) and clear local state. */
  signOut: () => Promise<void>;
}

// The in-flight boot, so a second call joins it instead of starting another.
//
// React StrictMode (dev) mounts the root twice, so App's `bootstrap()` effect
// fires twice within the same tick. Both calls read the same stored refresh
// token; the server ROTATES it on the first, so the second is a reuse and 401s.
// That loser cleared the token and set `unauthenticated`, which sent a reload of
// /hospitality to /welcome — and the winner, landing a moment later, bounced
// /welcome to /home. Every reload ended on the home tab.
let booting: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  accessToken: null,
  me: null,
  pendingTokens: null,

  bootstrap: () => {
    if (!booting) booting = runBootstrap(set).finally(() => (booting = null));
    return booting;
  },

  signInWithTokens: async (tokens) => {
    await saveRefreshToken(tokens.refresh_token);
    const me = await getMe(tokens.access_token);
    // Cached results are keyed without the user id, so another account must
    // never be served the previous one's tickets or card.
    if (get().me?.user.id !== me.user.id) queryClient.clear();
    set({ status: 'authenticated', accessToken: tokens.access_token, me });
    void adoptProfileLanguage(me.profile.language);
    if (useSettingsStore.getState().pushNotifications) {
      void registerPushToken(tokens.access_token);
    }
  },

  setMe: (me) => set({ me }),

  startOnboarding: (tokens) => set({ pendingTokens: tokens }),

  finishOnboarding: async () => {
    const tokens = get().pendingTokens;
    if (!tokens) return;
    await get().signInWithTokens(tokens);
    set({ pendingTokens: null });
  },

  signOut: async () => {
    const refresh = await getRefreshToken();
    if (refresh) {
      try {
        await logout(refresh);
      } catch {
        // best effort — clear locally regardless
      }
    }
    await clearRefreshToken();
    queryClient.clear();
    set({ status: 'unauthenticated', accessToken: null, me: null });
  },
}));

async function runBootstrap(set: (s: Partial<AuthState>) => void) {
  try {
    const refresh = await getRefreshToken();
    if (!refresh) {
      set({ status: 'unauthenticated', accessToken: null, me: null });
      return;
    }
    // Rotate on boot: exchange the stored refresh token for a fresh pair.
    const tokens = await refreshTokens(refresh);
    await saveRefreshToken(tokens.refresh_token);
    const me = await getMe(tokens.access_token);
    set({ status: 'authenticated', accessToken: tokens.access_token, me });
    void adoptProfileLanguage(me.profile.language);
    if (useSettingsStore.getState().pushNotifications) {
      void registerPushToken(tokens.access_token);
    }
  } catch {
    await clearRefreshToken();
    set({ status: 'unauthenticated', accessToken: null, me: null });
  }
}
